'use client'

import { useState } from 'react'
import { redirect, useSearchParams } from 'next/navigation'

import { WP_Term_Object } from '@/types/wp'

import SdgWizardNav from './SdgWizardNav'
import SdgWizardIntro from './SdgWizardIntro'
import SdgWizardCheckboxes from './SdgWizardCheckboxes'

import updateSdgPreferencesAction from './update-sdg-preferences'

export default function SdgWizard({
  initialState,
  roles,
  industries,
}: {
  initialState: {
    roles: number[] | string[]
    industries: number[] | string[]
  }
  roles: WP_Term_Object[]
  industries: WP_Term_Object[]
}) {
  const searchParams = useSearchParams()

  const startingPage = searchParams.get('page') || null
  let initialPage = 0

  if (startingPage === 'industries') {
    initialPage = 1
  } else if (startingPage === 'roles') {
    initialPage = 2
  }

  const [state, setState] = useState(initialState)
  const [activePage, setActivePage] = useState(initialPage)
  const [pageReached, setPageReached] = useState(initialPage)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleNextPage = () => {
    const validationFn =
      sdgPages[activePage]?.validationFn || (() => ({ valid: true, message: '' }))

    const { valid, message } = validationFn(state)

    if (!valid) {
      alert(message)
      return
    }

    // Last page, so handle submission after validation
    if (activePage === sdgPages.length - 1) {
      handleSubmit()
      return
    }

    // Go to next page
    setActivePage(activePage + 1)
    setPageReached(activePage + 1)
  }

  const handleSubmit = async () => {
    if (isSaving) {
      return
    }

    setIsSaving(true)
    setError(null)

    const response = await updateSdgPreferencesAction(state.industries, state.roles)

    if (response) {
      redirect('/industry/focus/sustainable-finance/sustainable-development/tool/my-results/')
    }

    setIsSaving(false)
    return
  }

  const sdgPages = [
    {
      id: 'intro',
      title: 'Intro',
      children: <SdgWizardIntro />,
    },
    {
      id: 'industries',
      title: 'Industries',
      children: (
        <div>
          <div className="mb-l flex flex-col items-center gap-xxs text-center">
            <p className="mb-0">
              Select the <strong>Industries</strong> most relevant to your investments and/or
              business
            </p>

            <p className="p--xsmall inline-block bg-grey-100 px-xxs py-[2px]">
              You can{' '}
              <button
                className="text-primary"
                onClick={() => {
                  handleNextPage()
                }}>
                skip this step
              </button>{' '}
              and select only some <strong>Roles</strong> instead
            </p>
          </div>

          <SdgWizardCheckboxes
            name="industries"
            choices={industries}
            state={state}
            setState={setState}
            cols={1}
          />
        </div>
      ),
    },
    {
      id: 'roles',
      title: 'Roles',
      children: (
        <div>
          <div className="mb-l flex flex-col items-center gap-xxs text-center">
            <p className="mb-0">
              Select the <strong>Roles</strong> most relevant to your investments and/or business
            </p>

            {state.industries.length > 0 ? (
              <p className="p--xsmall inline-block bg-grey-100 px-xxs py-[2px]">
                If you have selected some <strong>Industries</strong> you can{' '}
                <button
                  className="text-primary"
                  onClick={() => {
                    handleNextPage()
                  }}>
                  skip this step
                </button>
              </p>
            ) : null}
          </div>

          <SdgWizardCheckboxes name="roles" choices={roles} state={state} setState={setState} />
        </div>
      ),
      validationFn: (state: any) => {
        if (state.industries.length === 0 && state.roles.length === 0) {
          return {
            valid: false,
            message: 'Please select at least one industry or role.',
          }
        }

        return { valid: true }
      },
    },
  ]

  return (
    <div className={isSaving ? 'pointer-events-none animate-pulse cursor-wait' : ''}>
      <SdgWizardNav
        pages={sdgPages}
        activePage={activePage}
        setActivePage={setActivePage}
        pageReached={pageReached}
      />

      <SdgWizardPages
        handleNextPage={handleNextPage}
        sdgPages={sdgPages}
        activePage={activePage}
        handleSubmit={handleSubmit}
        isSaving={isSaving}
        error={error}>
        {sdgPages.map((page, index) => (
          <SdgWizardPage key={page.id} isActive={activePage === index}>
            {page.children}
          </SdgWizardPage>
        ))}
      </SdgWizardPages>
    </div>
  )
}

function SdgWizardPages({
  children,
  sdgPages,
  activePage,
  handleNextPage,
  handleSubmit,
  isSaving = false,
  error = null,
}: {
  children: React.ReactNode
  sdgPages: any[]
  activePage: number
  handleNextPage: () => void
  handleSubmit: () => void
  isSaving?: boolean
  error?: string | null
}) {
  return (
    <div className="wizard-pages">
      {children}

      <footer className="sticky bottom-0 left-0 right-0 z-10 border-y bg-white py-s">
        <div className="container">
          <div className="flex items-center justify-end">
            {error ? (
              <div className="py-m">
                <div className="notice notice--error">
                  <p>There was a problem saving your preferences, please try again.</p>
                </div>
              </div>
            ) : null}

            {activePage === sdgPages.length - 1 ? (
              <button className="btn btn--solid" onClick={handleSubmit}>
                {isSaving ? 'Saving...' : 'Submit'}
              </button>
            ) : (
              <button className="btn btn--outline" onClick={handleNextPage}>
                Next
              </button>
            )}
          </div>
        </div>
      </footer>
    </div>
  )
}

function SdgWizardPage({
  isActive = false,
  children,
}: {
  isActive: boolean
  children: React.ReactNode
}) {
  return <div className={`wizard-page container py-l ${isActive ? '' : 'hidden'}`}>{children}</div>
}
